import { useEffect, useMemo, useState } from "react";
import { X } from "lucide-react";
import { toast } from "react-hot-toast";
import {
  generateRandomHexColor,
  getAllPaletteColors,
  normalizeColorValue,
  pickRandomColor,
  resolveRegionColorPalette,
} from "../utils/teamColors";
import { TeamColorPicker } from "./TeamColorPicker";

interface Region {
  id: string;
  name: string;
  description?: string;
  isActive: boolean;
}

interface ExistingTeam {
  id: string;
  color?: string | null;
}

interface CreateTeamModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  teams: ExistingTeam[];
}

export function CreateTeamModal({
  isOpen,
  onClose,
  onSuccess,
  teams,
}: CreateTeamModalProps) {
  const [regions, setRegions] = useState<Region[]>([]);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    regionId: "",
    color: "",
    isActive: true
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loadingRegions, setLoadingRegions] = useState(false);
  const [colorTouched, setColorTouched] = useState(false);

  useEffect(() => {
    if (isOpen) {
      fetchRegions();
      setColorTouched(false);
    }
  }, [isOpen]);

  const selectedRegion = useMemo(
    () => regions.find((region) => region.id === formData.regionId),
    [formData.regionId, regions]
  );

  const usedColors = useMemo(() => {
    const set = new Set<string>();
    teams.forEach((team) => {
      const normalized = normalizeColorValue(team.color);
      if (normalized) {
        set.add(normalized);
      }
    });
    return set;
  }, [teams]);

  const palette = useMemo(() => {
    if (selectedRegion?.name) {
      return resolveRegionColorPalette(selectedRegion.name);
    }
    return getAllPaletteColors();
  }, [selectedRegion?.name]);

  const availablePalette = useMemo(
    () =>
      palette.filter(
        (color) => !usedColors.has(normalizeColorValue(color))
      ),
    [palette, usedColors]
  );

  const normalizedSelectedColor = normalizeColorValue(formData.color);
  const isDuplicateColor =
    Boolean(normalizedSelectedColor) && usedColors.has(normalizedSelectedColor);

  useEffect(() => {
    if (!isOpen || colorTouched) {
      return;
    }

    const nextColor =
      pickRandomColor(availablePalette) ??
      generateRandomHexColor(usedColors);

    setFormData((prev) => ({
      ...prev,
      color: nextColor,
    }));
  }, [availablePalette, colorTouched, isOpen, usedColors]);

  const fetchRegions = async () => {
    try {
      setLoadingRegions(true);
      const response = await fetch("/api/v1/regions");
      if (response.ok) {
        const data = await response.json();
        setRegions(data.data);
      }
    } catch (error) {
      console.error("Error fetching regions:", error);
      toast.error("Failed to load regions");
    } finally {
      setLoadingRegions(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      toast.error("Team name is required");
      return;
    }

    if (!formData.regionId) {
      toast.error("Please select a region");
      return;
    }

    if (!formData.color) {
      toast.error("Please select a team color");
      return;
    }

    if (isDuplicateColor) {
      toast.error("Team color already assigned");
      return;
    }

    try {
      setIsSubmitting(true);
      const response = await fetch("/api/v1/teams", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        toast.success("Team created successfully");
        setFormData({
          name: "",
          description: "",
          regionId: "",
          color: "",
          isActive: true,
        });
        onSuccess();
        onClose();
      } else {
        const errorData = await response.json();
        toast.error(errorData.error || "Failed to create team");
      }
    } catch (error) {
      console.error("Error creating team:", error);
      toast.error("Failed to create team");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === "checkbox" ? (e.target as HTMLInputElement).checked : value
    }));
  };

  const handleColorChange = (nextColor: string) => {
    setFormData((prev) => ({
      ...prev,
      color: nextColor,
    }));
    setColorTouched(true);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={onClose}></div>
        <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full">
          <div className="flex items-center justify-between p-6 border-b">
            <h3 className="text-lg font-medium text-gray-900">Create New Team</h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
          
          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                Team Name *
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                placeholder="e.g., Customer Support - NA"
                required
              />
            </div>

            <div>
              <label htmlFor="regionId" className="block text-sm font-medium text-gray-700">
                Region *
              </label>
              <select
                id="regionId"
                name="regionId"
                value={formData.regionId}
                onChange={handleChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                required
                disabled={loadingRegions}
              >
                <option value="">Select a region</option>
                {regions.map((region) => (
                  <option key={region.id} value={region.id}>
                    {region.name}
                  </option>
                ))}
              </select>
              {loadingRegions && (
                <p className="mt-1 text-sm text-gray-500">Loading regions...</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                Team Color *
              </label>
              <p className="mt-1 text-xs text-gray-500">
                A unique color is auto-selected. Open the picker to choose another.
              </p>
              <div className="mt-3">
                <TeamColorPicker
                  color={formData.color}
                  palette={palette}
                  usedColors={usedColors}
                  onChange={handleColorChange}
                />
              </div>
              {isDuplicateColor && (
                <p className="mt-2 text-xs text-rose-600">
                  This color is already assigned to another team.
                </p>
              )}
            </div>

            <div>
              <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                Description
              </label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                rows={3}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                placeholder="Optional description of the team"
              />
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="isActive"
                name="isActive"
                checked={formData.isActive}
                onChange={handleChange}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="isActive" className="ml-2 block text-sm text-gray-900">
                Active
              </label>
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting || loadingRegions || isDuplicateColor || !formData.color}
                className="px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md hover:bg-green-700 disabled:opacity-50"
              >
                {isSubmitting ? "Creating..." : "Create Team"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
